import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import DefaultButton from "../general/buttons/DefaultButton";
import UserAvatar from "../general/UserAvatar";
import {
    fetchFollowers,
    FollowerDTO,
    notifyPlaylistsChanged,
    removeFollower,
    respondToContributorRequest,
    setContributor,
} from "../../services/playlist";
import { notifyNotificationsChanged } from "../../services/notifications";
import { colors } from "../../theme";

interface PlaylistFollowersDialogProps {
    open: boolean;
    playlistId: number;
    playlistName: string;
    onClose: () => void;
}

/**
 * The owner's view of who follows their playlist, and who can add to it. Contributor
 * status is layered on following, so this list is also the only place a follower can be
 * promoted from - there's no way to grant it to someone who isn't following.
 */
export default function PlaylistFollowersDialog(props: PlaylistFollowersDialogProps) {
    const { open, playlistId, playlistName, onClose } = props;
    const [followers, setFollowers] = useState<FollowerDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState<number[]>([]);

    const load = useCallback(async () => {
        if (!open) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setFollowers(await fetchFollowers(playlistId));
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load followers");
        } finally {
            setLoading(false);
        }
    }, [open, playlistId]);

    useEffect(() => {
        load();
    }, [load]);

    const runAction = async (userId: number, action: () => Promise<unknown>) => {
        setPending(prev => [...prev, userId]);
        try {
            await action();
            // Refetch rather than patching locally: approving also clears the request
            // flag, and a promotion can settle a request that was filed in another tab.
            await load();
            notifyNotificationsChanged();
            notifyPlaylistsChanged();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't update this follower");
        } finally {
            setPending(prev => prev.filter(id => id !== userId));
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: colors.elevated,
                        border: `1px solid ${colors.line}`,
                        borderRadius: 3,
                    },
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                Contributors
                <Typography sx={{ fontSize: 13, color: colors.textSecondary, fontWeight: 400 }}>
                    {followers.length} follower{followers.length === 1 ? "" : "s"} of {playlistName}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {error && (
                    <Typography sx={{ color: colors.danger, fontSize: 13, mb: 1.5 }}>
                        {error}
                    </Typography>
                )}

                {!loading && followers.length === 0 && !error && (
                    <Typography sx={{ color: colors.textSecondary, fontSize: 14, py: 2 }}>
                        Nobody follows this playlist yet. People who follow it can be made
                        contributors here.
                    </Typography>
                )}

                {followers.map(follower => (
                    <Box
                        key={follower.userId}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            py: 1.25,
                            borderBottom: `1px solid ${colors.line}`,
                        }}
                    >
                        <UserAvatar
                            username={follower.username}
                            imageUrl={follower.profilePictureUrl}
                            size={36}
                        />
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 15 }} noWrap>
                                {follower.username}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: colors.textMuted }} noWrap>
                                {follower.contributor
                                    ? "Contributor"
                                    : follower.requested
                                        ? "Asked to contribute"
                                        : "Follower"}
                            </Typography>
                        </Box>

                        {follower.requested && !follower.contributor ? (
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <DefaultButton
                                    text="Approve"
                                    size="small"
                                    disabled={pending.includes(follower.userId)}
                                    onClick={() => runAction(follower.userId,
                                        () => respondToContributorRequest(playlistId, follower.userId, true))}
                                />
                                <DefaultButton
                                    text="Deny"
                                    variant="secondary"
                                    size="small"
                                    disabled={pending.includes(follower.userId)}
                                    onClick={() => runAction(follower.userId,
                                        () => respondToContributorRequest(playlistId, follower.userId, false))}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", gap: 1 }}>
                                {/* Two different things, so two buttons: demoting takes the
                                    contributor badge away but leaves them following, while
                                    removing drops them from the playlist entirely. */}
                                <DefaultButton
                                    text={follower.contributor ? "Demote" : "Make contributor"}
                                    variant={follower.contributor ? "secondary" : "primary"}
                                    size="small"
                                    disabled={pending.includes(follower.userId)}
                                    onClick={() => runAction(follower.userId,
                                        () => setContributor(playlistId, follower.userId, !follower.contributor))}
                                />
                                <DefaultButton
                                    text="Remove"
                                    variant="ghost"
                                    size="small"
                                    disabled={pending.includes(follower.userId)}
                                    onClick={() => runAction(follower.userId,
                                        () => removeFollower(playlistId, follower.userId))}
                                />
                            </Box>
                        )}
                    </Box>
                ))}
            </DialogContent>
        </Dialog>
    );
}
